import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatWindow } from '@/components/chat/chat-window';
import { ModeSelector } from '@/components/layout/mode-selector';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Search, MessageSquare, Bot, UserPlus, Send, Loader2, Sparkles } from 'lucide-react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useAppMode } from '@/hooks/use-app-mode';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { VoiceInput } from '@/components/ai/voice-input';
import { openAIService, OpenAIMessage } from '@/lib/ai/openai-service';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { v4 as uuidv4 } from 'uuid';
import type { ChatConversation } from '@/lib/types/chat';

// Quick AI Assistant component for Traditional mode
function QuickAIAssistant() {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleVoiceInput = (text: string) => {
    setInput(text);
    toast({
      title: "Voice transcribed",
      description: "Your voice has been converted to text. Press send to submit.",
    });
  };
  
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    setIsLoading(true);
    setResponse('');
    
    try {
      // Create OpenAI message payload
      const openAIMessages: OpenAIMessage[] = [
        {
          role: 'system',
          content: `You are FlexHelp AI, an assistant specialized in property management, maintenance guidance, and regulatory compliance for the FlexHelp platform.
          
Current user: ${user?.name} (${user?.role})
Time: ${new Date().toISOString()}
          
When asked about properties, maintenance issues, or compliance requirements:
1. Focus on providing practical, actionable advice
2. Reference industry best practices and regulations where applicable
3. Consider property type and location when giving compliance advice
4. Be conversational but professional
5. Prioritize user safety and regulatory compliance in your recommendations
          
Keep responses brief and to the point, as this is the quick assistance mode.`,
        },
        { role: 'user', content: input },
      ];

      // Generate response from OpenAI
      const aiResponse = await openAIService.generateCompletion(openAIMessages);

      if (aiResponse.error) {
        throw new Error(aiResponse.error);
      }

      setResponse(aiResponse.content);
      setInput('');
    } catch (error) {
      console.error('Error processing query:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to get AI response',
      });
      setResponse('Sorry, I encountered an error. Please try again or visit the full FlexHelp AI page for more assistance.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const isAIConfigured = openAIService.isReady();
  
  if (!isAIConfigured) {
    return (
      <Card className="p-4 flex flex-col items-center text-center space-y-3">
        <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-3">
          <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-lg font-medium">AI Not Configured</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Ask your administrator to configure the FlexHelp AI feature.
        </p>
        <Button 
          size="sm" 
          variant="outline" 
          className="mt-2"
          onClick={() => navigate('/flexhelp-ai')}
        >
          Switch to Full AI Mode
        </Button>
      </Card>
    );
  }
  
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <h3 className="font-medium">Quick AI Assistant</h3>
      </div>
      
      {response && (
        <div className="mb-4 p-3 bg-card/60 backdrop-blur-sm rounded-lg text-card-foreground">
          <div className="flex items-start gap-3">
            <Avatar className="bg-primary/10 mt-1">
              <AvatarFallback>
                <Bot className="h-5 w-5 text-primary" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-sm whitespace-pre-wrap break-words">
              {response}
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSend} className="flex gap-2">
        <Input 
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a quick question..."
          className="flex-1"
          disabled={isLoading}
        />
        <VoiceInput 
          onTranscription={handleVoiceInput}
          isDisabled={isLoading}
        />
        <Button type="submit" disabled={isLoading || !input.trim()}>
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
      
      <div className="mt-3 text-center">
        <Button 
          variant="link" 
          size="sm" 
          className="text-xs text-muted-foreground"
          onClick={() => navigate('/flexhelp-ai')}
        >
          Switch to full FlexHelp AI for more features
        </Button>
      </div>
    </Card>
  );
}

// Mock conversations for demonstration
const mockConversations: ChatConversation[] = [
  {
    id: '1',
    type: 'direct',
    participants: [
      {
        id: '2',
        name: 'John Doe',
        status: 'online',
        createdAt: new Date('2024-01-01T00:00:00Z'),
        updatedAt: new Date('2024-01-01T00:00:00Z'),
      }
    ],
    messages: [],
    unreadCount: 2,
    lastMessage: {
      id: '1',
      senderId: '2',
      content: 'Hey, are you available for a quick task?',
      timestamp: new Date(),
      type: 'text',
      status: 'delivered',
    }
  },
  {
    id: '2',
    type: 'direct',
    participants: [
      {
        id: '3',
        name: 'Sarah Johnson',
        status: 'away',
        createdAt: new Date('2024-01-01T00:00:00Z'),
        updatedAt: new Date('2024-01-01T00:00:00Z'),
      }
    ],
    messages: [],
    unreadCount: 0,
    lastMessage: {
      id: '2',
      senderId: '3',
      content: "I've completed the inspection for 123 Main St.",
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      type: 'text',
      status: 'read',
    }
  },
  {
    id: '3',
    type: 'direct',
    participants: [
      {
        id: '4',
        name: 'Mike Davis',
        status: 'offline',
        createdAt: new Date('2024-01-01T00:00:00Z'),
        updatedAt: new Date('2024-01-01T00:00:00Z'),
      }
    ],
    messages: [],
    unreadCount: 0,
    lastMessage: {
      id: '3',
      senderId: '4',
      content: 'The repair has been scheduled for tomorrow at 2pm.',
      timestamp: new Date(Date.now() - 1000 * 60 * 120),
      type: 'text',
      status: 'read',
    }
  }
];

export function ChatPage() {
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation>({
    id: '',
    type: 'direct',
    participants: [],
    messages: [],
    unreadCount: 0,
    lastMessage: {
      id: '',
      senderId: '',
      content: '',
      timestamp: new Date(),
      type: 'text',
      status: 'delivered',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  const [search, setSearch] = useState('');
  const { mode } = useAppMode();
  const navigate = useNavigate();

  // Redirect to FlexHelp AI page if in AI mode
  useEffect(() => {
    if (mode === 'ai') {
      navigate('/flexhelp-ai');
    }
  }, [mode, navigate]);

  const filteredConversations = mockConversations.filter(
    conv => conv.participants.some(p => 
      p.name.toLowerCase().includes(search.toLowerCase())
    )
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-emerald-500';
      case 'away':
        return 'bg-amber-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatTime = (timestamp: Date) => {
    const now = new Date();
    const isToday = timestamp.getDate() === now.getDate() && 
                    timestamp.getMonth() === now.getMonth() && 
                    timestamp.getFullYear() === now.getFullYear();
    
    if (isToday) {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    return timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleDateSelect = (date: Date) => {
    setSelectedConversation({
      ...selectedConversation,
      createdAt: date,
      updatedAt: date,
    });
  };

  return (
    <div className="container h-[calc(100vh-8rem)] py-6">
      {/* Mode Selector */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <ModeSelector />
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col gap-4"
      >
        {/* Quick AI Assistant (only in traditional mode) */}
        {mode === 'traditional' && (
          <QuickAIAssistant />
        )}
        
        <ResizablePanelGroup direction="horizontal" className="min-h-full rounded-xl border overflow-hidden">
          <ResizablePanel defaultSize={25} minSize={20} maxSize={40}>
            <Card variant="glass" className="h-full rounded-none border-0">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 text-primary" />
                  </div>
                  <h2 className="font-medium">Conversations</h2>
                </div>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="p-4 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search conversations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-background/50 backdrop-blur-sm"
                  />
                </div>
              </div>
              
              <ScrollArea className="h-[calc(100vh-14rem)]">
                <AnimatePresence>
                  {filteredConversations.map((conversation, index) => (
                    <motion.div
                      key={conversation.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className={`group flex items-center gap-3 p-4 cursor-pointer transition-all hover:bg-background/50 ${
                        selectedConversation.id === conversation.id ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => setSelectedConversation(conversation)}
                    >
                      <div className="relative">
                        <Avatar className="border border-background">
                          <AvatarImage src={conversation.participants[0].avatar} alt={conversation.participants[0].name} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {conversation.participants[0].name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${getStatusColor(conversation.participants[0].status)}`}></span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium truncate">
                            {conversation.participants[0].name}
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conversation.lastMessage?.timestamp || new Date())}
                          </span>
                        </div>
                        {conversation.lastMessage && (
                          <div className="flex items-center">
                            <p className="text-sm text-muted-foreground truncate">
                              {conversation.lastMessage.content}
                            </p>
                            {conversation.unreadCount > 0 && (
                              <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </ScrollArea>
            </Card>
          </ResizablePanel>
          <ResizableHandle withHandle />
          
          <ResizablePanel defaultSize={75}>
            <div className="h-full relative">
              <div className="absolute inset-0 -z-10 bg-grid-black/5 dark:bg-grid-white/5">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
              </div>
              
              {selectedConversation ? (
                <ChatWindow
                  chat={{
                    id: selectedConversation.id,
                    title: selectedConversation.title || selectedConversation.participants[0].name,
                    participants: selectedConversation.participants,
                    unreadCount: selectedConversation.unreadCount,
                    type: selectedConversation.type || 'direct',
                    createdAt: selectedConversation.createdAt,
                    updatedAt: selectedConversation.updatedAt,
                    lastMessage: selectedConversation.lastMessage
                  }}
                  messages={[]}
                  onSendMessage={() => {}}
                  onSendAttachment={async (file) => {
                    try {
                      // handleSendAttachment(file);
                    } catch (error) {
                      console.error('Error sending attachment:', error);
                    }
                  }}
                  conversationId={selectedConversation.id}
                  title={selectedConversation.title || selectedConversation.participants[0].name}
                  participants={selectedConversation.participants}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="rounded-full bg-primary/10 p-5 mb-4">
                    <MessageSquare className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-2">No Conversation Selected</h3>
                  <p className="text-muted-foreground mb-6 text-center max-w-xs">
                    Select a conversation from the sidebar or start a new one.
                  </p>
                  <Button variant="outline" className="gap-2">
                    <UserPlus className="h-4 w-4" /> 
                    <span>New Conversation</span>
                  </Button>
                </div>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </motion.div>
    </div>
  );
}